import { SyntheticEvent, useCallback } from 'react';
import type { RootStateOrAny as State } from 'react-redux';
import type JSCookie from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import {
  addErrorToReport as importedAddErrorToReport,
  // https://github.aexp.com/amex-eng/one-app-dependencies/blob/f464bc31f473f15951521bbceb8426e688400568/packages/one-app/one-app-ducks/src/index.js#L36
  // @ts-expect-error -- getCookies is missing a type declaration in AmEx types, but it exists!
  getCookies as importedGetCookies,
} from '@americanexpress/one-app-ducks';

import type { Refresh } from '../../../components/types';
import { MODULE_NAME } from '../../../constants';

const addErrorToReport = importedAddErrorToReport as (
  /* Contrary to the type definition in One App Ducks, error can be null.
   * This logging function is being used for more than errors despite the intent.
   * Even if the error is null, it will not err; it just won't generate a stack trace. */
  error: SyntheticEvent<HTMLVideoElement, Event> | Error | null,
  otherData?: Parameters<typeof importedAddErrorToReport>[1],
) => ReturnType<typeof importedAddErrorToReport>;

export type Cookies = ReturnType<typeof JSCookie.get>;
const getCookies = importedGetCookies as (state: State) => Cookies | undefined;

export type SplunkLogVariables<More = object> = {
  error: SyntheticEvent<HTMLVideoElement, Event> | Error | null,
  apiName?: string,
  responseCode?: number | null,
  correlationId?: string | null,
  message: string,
  requestBody: object,
  refresh: Refresh,
} & More;

const useSplunkLogger = () => {
  const cookies = useSelector(getCookies);
  const dispatch = useDispatch();

  return useCallback(<More = Record<string, never>>(ok: boolean, {
    apiName,
    error,
    responseCode,
    message,
    correlationId,
    requestBody,
    refresh,
    ...rest
  }: SplunkLogVariables<More>) => {
    dispatch(addErrorToReport(error, {
      ...rest,
      moduleId: MODULE_NAME,
      additionalMessage: message,
      accountToken: refresh.accountToken,
      journeyIdentifier: refresh.status,
      backendResourceName: apiName || null,
      backendHttpResponseCode: responseCode || null,
      correlationId: correlationId || null,
      rxVisitor: cookies?.rxVisitor,
      publicGuid: cookies?.blueboxpublic,
      route: window?.location.pathname,
      requestPayload: requestBody,
      status: ok ? 'success' : 'failure',
    }));
  }, [cookies?.blueboxpublic, cookies?.rxVisitor, dispatch]);
};

export default useSplunkLogger;
